import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
    Box, Typography, Button, Paper, Table, TableHead, TableBody,
    TableRow, TableCell, Chip, IconButton, Dialog, DialogTitle,
    DialogContent, DialogActions, TextField, CircularProgress, Alert,
    FormControl, InputLabel, Select, OutlinedInput, MenuItem,
    Checkbox, ListItemText, InputAdornment,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { toast } from 'react-toastify';
import { directorAPI, publicAPI } from '../../services/api';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import useAuth from '../../hooks/useAuth';

export default function DirectorModules() {
    const { user } = useAuth();
    const isAdmin = user?.role === 'admin';

    const PER_PAGE = 50;

    const [modules, setModules] = useState([]);
    const [loading, setLoading] = useState(true);
    const [initialLoading, setInitialLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [page, setPage] = useState(1);
    const [lastPage, setLastPage] = useState(1);
    const [dialog, setDialog] = useState(false);
    const [istas, setIstas] = useState([]);
    const [form, setForm] = useState({ name: '', code: '', description: '', masse_horaire: 0, is_active: true, ista_ids: [] });
    const [editing, setEditing] = useState(null);
    const [deleteId, setDeleteId] = useState(null);
    const [error, setError] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');

    const scrollRef = useRef(null);

    // Load istas once
    useEffect(() => {
        publicAPI.getIstas().then(res => setIstas(res.data)).catch(console.error);
    }, []);

    // Debounce the search input (server-side search across ALL modules)
    useEffect(() => {
        const t = setTimeout(() => setDebouncedSearch(searchTerm.trim()), 350);
        return () => clearTimeout(t);
    }, [searchTerm]);

    // Fetch a page of modules; page 1 replaces, others append
    const fetchModules = useCallback(async (pageToLoad, search) => {
        if (pageToLoad === 1) setLoading(true); else setLoadingMore(true);
        try {
            const res = await directorAPI.getModules({ per_page: PER_PAGE, page: pageToLoad, search: search || undefined });
            const { data, current_page, last_page } = res.data;
            setModules(prev => pageToLoad === 1 ? data : [...prev, ...data]);
            setPage(current_page);
            setLastPage(last_page);
        } catch (err) { console.error(err); }
        finally { setLoading(false); setLoadingMore(false); setInitialLoading(false); }
    }, []);

    // Reload from page 1 whenever the (debounced) search changes
    useEffect(() => {
        if (scrollRef.current) scrollRef.current.scrollTop = 0;
        fetchModules(1, debouncedSearch);
    }, [debouncedSearch, fetchModules]);

    const loadData = () => fetchModules(1, debouncedSearch);

    // Infinite scroll: load next page when near the bottom
    const handleScroll = (e) => {
        const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
        if (scrollHeight - scrollTop - clientHeight < 300 && !loadingMore && page < lastPage) {
            fetchModules(page + 1, debouncedSearch);
        }
    };

    const handleOpen = (mod = null) => {
        if (mod) {
            setEditing(mod.id);
            setForm({
                name: mod.name,
                code: mod.code,
                description: mod.description || '',
                masse_horaire: mod.masse_horaire || 0,
                is_active: mod.is_active,
                ista_ids: mod.istas ? mod.istas.map(i => i.id) : []
            });
        } else {
            setEditing(null);
            setForm({ name: '', code: '', description: '', masse_horaire: 0, is_active: true, ista_ids: [] });
        }
        setError('');
        setDialog(true);
    };

    const handleSave = async () => {
        setError('');
        try {
            if (editing) {
                await directorAPI.updateModule(editing, form);
                toast.success('Module modifié');
            } else {
                await directorAPI.createModule(form);
                toast.success('Module créé');
            }
            setDialog(false);
            loadData();
        } catch (err) {
            setError(err.response?.data?.message || 'Erreur');
        }
    };

    const handleDelete = async () => {
        try {
            await directorAPI.deleteModule(deleteId);
            toast.success('Module supprimé');
            setDeleteId(null);
            loadData();
        } catch (err) {
            toast.error('Erreur lors de la suppression');
        }
    };

    if (initialLoading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}><CircularProgress /></Box>;

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3, alignItems: 'center', gap: 2 }}>
                <Typography variant="h5" fontWeight={700}>Gérer les modules</Typography>
                
                <Box sx={{ display: 'flex', gap: 2, flex: 1, justifyContent: 'flex-end' }}>
                    <TextField 
                        size="small" 
                        placeholder="Rechercher un module..." 
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        sx={{ width: 300, bgcolor: 'white', borderRadius: 2 }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon sx={{ color: 'text.secondary' }} />
                                </InputAdornment>
                            ),
                        }}
                    />
                    <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpen()}
                        sx={{ background: 'linear-gradient(135deg, #1a5276, #2e86c1)', textTransform: 'none', borderRadius: 2 }}>
                        Ajouter module
                    </Button>
                </Box>
            </Box>

            <Paper sx={{ borderRadius: 3, overflow: 'hidden' }}>
                <Box ref={scrollRef} onScroll={handleScroll} sx={{ maxHeight: 'calc(100vh - 230px)', overflow: 'auto' }}>
                <Table stickyHeader>
                    <TableHead sx={{ bgcolor: '#f8f9fa' }}>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 700 }}>Code</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Nom</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>MHT</TableCell>
                            {isAdmin && <TableCell sx={{ fontWeight: 700 }}>ISTAs Affectés</TableCell>}
                            <TableCell sx={{ fontWeight: 700 }}>Filières</TableCell>
                            <TableCell sx={{ fontWeight: 700 }} align="right">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {modules.map(m => (
                            <TableRow key={m.id} hover>
                                <TableCell><Chip label={m.code} size="small" variant="outlined" /></TableCell>
                                <TableCell><Typography variant="body2" fontWeight={600}>{m.name}</Typography></TableCell>
                                <TableCell><Chip label={`${m.masse_horaire}h`} size="small" color="primary" variant="outlined" /></TableCell>
                                {isAdmin && (
                                    <TableCell>
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                            {m.istas?.map(i => (
                                                <Chip key={i.id} label={i.code} size="small" color="info" variant="outlined" />
                                            ))}
                                            {(!m.istas || m.istas.length === 0) && <Typography variant="caption" color="text.secondary">Aucun</Typography>}
                                        </Box>
                                    </TableCell>
                                )}
                                <TableCell><Chip label={m.filieres_count || 0} size="small" variant="outlined" /></TableCell>
                                <TableCell align="right">
                                    <IconButton onClick={() => handleOpen(m)} color="primary" size="small"><EditIcon fontSize="small" /></IconButton>
                                    <IconButton onClick={() => setDeleteId(m.id)} color="error" size="small"><DeleteIcon fontSize="small" /></IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
                {(loadingMore || loading) && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}><CircularProgress size={28} /></Box>
                )}
                {!loading && !loadingMore && modules.length === 0 && (
                    <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>Aucun module trouvé</Box>
                )}
                </Box>
            </Paper>

            <Dialog open={dialog} onClose={() => setDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>{editing ? 'Modifier le module' : 'Nouveau module'}</DialogTitle>
                <DialogContent>
                    {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                    <TextField fullWidth label="Nom" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} sx={{ mt: 1, mb: 2 }} />
                    <TextField fullWidth label="Code" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} sx={{ mb: 2 }} />
                    <TextField fullWidth type="number" label="Masse Horaire Totale" value={form.masse_horaire} onChange={(e) => setForm({ ...form, masse_horaire: parseInt(e.target.value) })} sx={{ mb: 2 }} />
                    <TextField fullWidth multiline rows={2} label="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} sx={{ mb: 2 }} />

                    {isAdmin && (
                        <FormControl fullWidth margin="dense">
                            <InputLabel>Affecter à des ISTAs</InputLabel>
                            <Select
                                multiple
                                value={form.ista_ids}
                                onChange={(e) => setForm({ ...form, ista_ids: e.target.value })}
                                input={<OutlinedInput label="Affecter à des ISTAs" />}
                                renderValue={(selected) => (
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                        {selected.map((value) => (
                                            <Chip key={value} label={istas.find(i => i.id === value)?.code || value} size="small" />
                                        ))}
                                    </Box>
                                )}
                            >
                                {istas.map((i) => (
                                    <MenuItem key={i.id} value={i.id}>
                                        <Checkbox checked={form.ista_ids.indexOf(i.id) > -1} />
                                        <ListItemText primary={`${i.code} - ${i.nom}`} />
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialog(false)}>Annuler</Button>
                    <Button onClick={handleSave} variant="contained" sx={{ bgcolor: '#1a5276' }}>Enregistrer</Button>
                </DialogActions>
            </Dialog>

            <ConfirmDialog 
                open={!!deleteId} 
                title="Supprimer le module ?" 
                message="Toutes les données liées seront conservées mais le module sera supprimé."
                onConfirm={handleDelete}
                onCancel={() => setDeleteId(null)}
            />
        </Box>
    );
}
